import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { LessonStatus } from '@lb/api/mylb/enums';

/**
 * The instant a lesson's edit window opens — its start time. Null when the lesson has no usable
 * start date, i.e. there is no deadline to wait for.
 */
export function getLessonEditWindowOpensAt(lesson: Pick<ILessonListItem, 'lessonDate'> | null | undefined): Date | null {
	const startTime = new Date(lesson?.lessonDate ?? '').getTime();

	if (Number.isNaN(startTime)) {
		return null;
	}

	return new Date(startTime);
}

/**
 * Whether a lesson has started. Instructors write a lesson up while they are still teaching it, so
 * the window opens with the lesson rather than at its scheduled end.
 *
 * Compared as instants, so the lesson's timezone does not enter into it. A lesson with a missing or
 * unparseable start date counts as open rather than locking the instructor out of a lesson that has
 * no start to measure from.
 */
export function isLessonEditWindowOpen(lesson: Pick<ILessonListItem, 'lessonDate'> | null | undefined, now: Date = new Date()): boolean {
	const opensAt = getLessonEditWindowOpensAt(lesson);

	if (opensAt === null) {
		return true;
	}

	return now.getTime() >= opensAt.getTime();
}

/**
 * Whether a lesson may be *completed* — the act that records attendance and the invoice. Open from
 * the moment the lesson starts, and permanently open on an already-completed lesson so its write-up
 * stays saveable afterwards.
 *
 * Deliberately narrower than "may the details be typed in": instructors pre-populate a lesson they
 * plan ahead and save it as a draft, which writes detail rows only. Kept here as a pure function so
 * the completion rule — the one thing this feature must not loosen — is pinned by tests.
 */
export function canCompleteLesson(
	lesson: Pick<ILessonListItem, 'lessonDate' | 'lessonStatusID'> | null | undefined,
	now: Date = new Date()
): boolean {
	if (lesson?.lessonStatusID === LessonStatus.Completed) {
		return true;
	}

	return isLessonEditWindowOpen(lesson, now);
}
